import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { appConst } from "../AppConst";
import { useAuth } from "../hooks/useAuth";

type HubContextType = {
  children: React.ReactNode;
};

type HubStoreType = {
  _connectionServer: HubConnection | null;
};

const HubContext = createContext({} as HubStoreType);
const useHubContext = () => useContext(HubContext);
const RECONNECT_DELAY = 5000;

const HubProvider = ({ children }: HubContextType) => {
  const { user } = useAuth();
  // Connection giữ trong ref để không tạo lại khi re-render và không trigger useEffect lặp.
  const connectionRef = useRef<HubConnection | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connectionServer, setConnectionServer] = useState<HubConnection | null>(null);

  useEffect(() => {
    if (!user) return;

    // Dev: FILEPATH_BASE_URL = host API tuyệt đối (https://localhost:44318, CROSS-ORIGIN → cookie sid
    // không tự gửi) nên phải đính ?userId= để server định danh. Prod: same-origin, trình duyệt gửi
    // cookie sid tự động → server lấy danh tính từ phiên, KHÔNG cần (và KHÔNG nên) lộ GUID trên URL.
    // Bảo hiểm: nếu build LỠ nhúng host localhost (quên set REACT_APP_FILEPATH_BASE_URL cho env prod)
    // nhưng trang đang chạy trên DOMAIN THẬT → coi như same-origin, tránh SignalR trỏ localhost gây CORS.
    const apiBase = appConst.FILEPATH_BASE_URL;
    const isHttp = !!apiBase && apiBase.startsWith("http");
    const baseIsLocalhost = isHttp && /localhost|127\.0\.0\.1/i.test(apiBase);
    const pageIsLocalhost = /localhost|127\.0\.0\.1/i.test(window.location.hostname);
    const isCrossOrigin = isHttp && !(baseIsLocalhost && !pageIsLocalhost);
    const domain = isCrossOrigin ? apiBase : window.location.origin;
    const hubUrl = isCrossOrigin
      ? domain + "/hubs/process?userId=" + user.id.toString()
      : domain + "/hubs/process";

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;
    setConnectionServer(connection);

    let isCancelled = false;

    const startConnection = async () => {
      // Chỉ start khi đang Disconnected — tránh "Cannot start a HubConnection that is not in the 'Disconnected' state".
      if (connection.state !== HubConnectionState.Disconnected) return;
      try {
        await connection.start();
      } catch {
        if (isCancelled) return;
        // Lên lịch retry, lưu timer id để clear khi unmount.
        reconnectTimerRef.current = setTimeout(startConnection, RECONNECT_DELAY);
      }
    };

    startConnection();

    return () => {
      isCancelled = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // stop() trả Promise — không await để cleanup chạy đồng bộ; lỗi (nếu có) bỏ qua an toàn.
      connection.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [user]);

  const store: HubStoreType = {
    _connectionServer: connectionServer,
  };

  return <HubContext.Provider value={store}>{children}</HubContext.Provider>;
};

export { HubProvider, useHubContext };
